import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { AuthProvider } from './contexts/AuthContext';
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import AuthCallback from './pages/AuthCallback';
import Login from './pages/Login';
import ProfileSetup from './pages/ProfileSetup';
import Connection from './pages/Connection';
import MoodSync from './pages/MoodSync';
import Brownies from './pages/Brownies';
import Reminders from './pages/Reminders';
import ConflictResolution from './pages/ConflictResolution';
import Settings from './pages/Settings';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogAdmin from './pages/BlogAdmin';
import Quiz from './pages/Quiz';
import FeatureLanding from './pages/FeatureLanding';
import GoRedirect from './pages/GoRedirect';
import ConnectLanding from './pages/ConnectLanding';
import AdminStats from './pages/AdminStats';
import NotFound from './pages/NotFound';
import NetworkStatus from './components/NetworkStatus';
import NotificationBanner from './components/NotificationBanner';
import { Toaster } from './components/ui/sonner';
import './App.css';

axios.defaults.withCredentials = true;

function ScrollHandler() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const targetId = hash.replace('#', '');
      const scrollTimer = setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(scrollTimer);
    } else {
      const root = document.getElementById('root');
      if (root) root.scrollTo({ top: 0, behavior: 'instant' });
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);

  return null;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Landing Page & Marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost isLegacyBlogPath={true} />} />
      <Route path="/admin/blog" element={<BlogAdmin />} />
      <Route path="/quiz/:quizId" element={<Quiz />} />
      <Route path="/never-forget-anything" element={<FeatureLanding pageId="never-forget-anything" />} />
      <Route path="/solve-fights-in-5-minutes" element={<FeatureLanding pageId="solve-fights-in-5-minutes" />} />
      <Route path="/love-with-intentionality" element={<FeatureLanding pageId="love-with-intentionality" />} />
      <Route path="/stay-connected-in-60-seconds" element={<FeatureLanding pageId="stay-connected-in-60-seconds" />} />
      
      {/* Deep Linking & Partner Connect Routes */}
      <Route path="/connect" element={<ConnectLanding />} />
      <Route path="/connect/:code" element={<ConnectLanding />} />
      <Route path="/pair" element={<ConnectLanding />} />
      <Route path="/pair/:code" element={<ConnectLanding />} />
      <Route path="/join" element={<ConnectLanding />} />
      <Route path="/join/:code" element={<ConnectLanding />} />
      <Route path="/invite" element={<ConnectLanding />} />
      <Route path="/invite/:code" element={<ConnectLanding />} />

      {/* Social short links (tracked redirects) */}
      {['ig', 'fb', 'tt', 'rd', 'x', 'th', 'yt'].map((s) => (
        <Route key={s} path={`/${s}`} element={<GoRedirect />} />
      ))}
      <Route path="/admin" element={<Navigate to="/admin/stats" replace />} />
      <Route path="/admin/stats" element={<AdminStats />} />
      <Route path="/admin/analytics" element={<Navigate to="/admin/stats" replace />} />
      <Route path="/admin/posts" element={<Navigate to="/admin/blog" replace />} />
      
      {/* App Routes */}
      <Route path="/app" element={<Onboarding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/profile-setup" element={<ProfileSetup />} />
      <Route path="/connection" element={<Connection />} />
      <Route path="/reminders" element={<Reminders />} />
      <Route path="/moodsync" element={<MoodSync />} />
      <Route path="/brownies" element={<Brownies />} />
      <Route path="/conflict" element={<ConflictResolution />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/dashboard" element={<Navigate to="/reminders" replace />} />
      
      {/* Dynamic Root-Level Blog Posts (e.g. /:slug) */}
      <Route path="/:slug" element={<BlogPost />} />

      {/* 404 Catch-All Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollHandler />
            <NetworkStatus />
            <NotificationBanner />
            <AppRouter />
            <Toaster position="top-center" />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
